import {
  hasCaliforniaAddressOrLocation,
  hasCaliforniaOfficeLocation,
} from "../../Practice/helpers"
import {
  Location,
  PhoneEmailFax,
  Practice,
  PracticeAddress,
  PracticeContact,
  TaxInformation,
} from "../../Practice/schema"
import { PracticeProfileStep, PracticeStepNameEnum } from "../schema"

export const isPracticeAddressComplete = (
  practiceAddress: PracticeAddress | undefined,
): boolean => {
  return (
    !!practiceAddress &&
    !!practiceAddress.addressOne &&
    !!practiceAddress.city &&
    !!practiceAddress.stateOrTerritory &&
    !!practiceAddress.county &&
    !!practiceAddress.zipCode
  )
}

export const isPracticeContactComplete = (
  practiceContact: PracticeContact | undefined,
): boolean => {
  return (
    !!practiceContact &&
    !!practiceContact.primaryEmail &&
    practiceContact.phones.filter(
      (phone: PhoneEmailFax) => !!phone.value && !!phone.type,
    ).length > 0
  )
}

export const isLocationAddressComplete = (
  location: Location | undefined,
): boolean => {
  return (
    !!location &&
    !!location.line1 &&
    !!location.city &&
    !!location.stateOrTerritory &&
    !!location.postalCode
  )
}

export const isTaxInformationComplete = (
  taxInformation: TaxInformation | undefined,
): boolean => {
  return (
    !!taxInformation &&
    !!taxInformation.taxOrganizationType &&
    !!taxInformation.governmentTaxId &&
    !!taxInformation.taxOrganizationType &&
    !!taxInformation.legalName
  )
}

export const isLocationContactComplete = (
  location: Location | undefined,
): boolean => {
  if (!!location) {
    const phonesComplete =
      location.phones.filter(
        (phone) => phone.number && phone.locationPhoneTypeDisplayId,
      ).length > 0
    const emailComplete =
      location.emails.filter((email) => email.address).length > 0
    return phonesComplete && emailComplete
  } else {
    return false
  }
}

export const isLocationDetailsComplete = (
  location: Location | undefined,
): boolean => {
  return !!location?.officeRequirementsCertified
}

export const practiceAgreementIsRequired = (practice: Practice): boolean => {
  const hasMadeOnlineServicesInCaliforniaSelection =
    practice.hasOnlineServicesInCalifornia !== null
  const practiceBillingAddress = practice.practiceInfo.practiceAddress
  const practiceHasCaliforniaBillingAddress =
    practiceBillingAddress?.stateOrTerritory === "California"
  return (
    !practiceHasCaliforniaBillingAddress &&
    hasMadeOnlineServicesInCaliforniaSelection
  )
}

export const practiceAgreementComplete = (
  practice: Practice,
  allowStale = false,
): boolean => {
  const practiceAgreement = practice.practiceAgreement
  const practiceAgreementComplete =
    practiceAgreement &&
    !!practice.practiceAgreement.practiceAgreement.signedFileId &&
    (allowStale || !practice.isPracticeAgreementStale)

  return practiceAgreementComplete
}

/**
 * US 20046 - behind FeatureFlag.IsResetRemoveAgreements20047Enabled.
 * The Standard Practice Agreement is required for every practice once the
 * California virtual services question has been answered (AC3). The California
 * billing address no longer excludes a practice from signing it.
 *
 * Takes the selection as an argument rather than reading it off the practice so
 * callers can drive display off the unsaved radio button value.
 */
export const practiceAgreementIsRequiredV2 = (
  providesOnlineServicesInCalifornia: boolean | null,
): boolean => providesOnlineServicesInCalifornia !== null

/**
 * US 20046 - behind FeatureFlag.IsResetRemoveAgreements20047Enabled.
 * Knox-Keene is driven by California office locations and the virtual services
 * answer. The billing address no longer triggers it (AC2, AC4, AC5).
 */
export const knoxKeeneIsRequiredV2 = (
  practice: Practice,
  providesOnlineServicesInCalifornia: boolean | null,
): boolean =>
  providesOnlineServicesInCalifornia !== null &&
  (providesOnlineServicesInCalifornia === true ||
    hasCaliforniaOfficeLocation(practice))

export const knoxKeeneIsRequired = (practice: Practice): boolean => {
  const hasOnlineServiceInCalifornia =
    practice.hasOnlineServicesInCalifornia === true
  const practiceHasCaliforniaAddressOrLocation =
    hasCaliforniaAddressOrLocation(practice)
  return hasOnlineServiceInCalifornia || practiceHasCaliforniaAddressOrLocation
}

export const knoxKeeneComplete = (
  practice: Practice,
  allowStale = false,
): boolean => {
  const practiceAgreement = practice.practiceAgreement
  const knoxKeeneComplete =
    practiceAgreement &&
    !!practice.practiceAgreement.knoxKeeneAgreement.signedFileId &&
    (allowStale || !practice.isKnoxKeeneStale)
  return knoxKeeneComplete
}

/**
 * Checks if the practice agreement step is complete for a given practice.
 * Note: This accounts for both the practice agreement and Knox-Keene agreement requirements.
 *       Additionally, we allow for stale practice/provider agreements to be considered complete
 *       as long as they were previously signed.
 * @param practice The practice to check.
 * @returns True if the practice agreement step was previously completed, false otherwise.
 */
export const isPracticeStepEffectiveComplete = (
  practice: Practice,
): boolean => {
  const isPracticeAgreementComplete =
    !practiceAgreementIsRequired(practice) ||
    practiceAgreementComplete(practice, true)
  const isKnoxKeeneComplete =
    !knoxKeeneIsRequired(practice) || knoxKeeneComplete(practice, true)
  return isPracticeAgreementComplete && isKnoxKeeneComplete
}

/**
 * US 20046 - behind FeatureFlag.IsResetRemoveAgreements20047Enabled.
 * Same contract as isPracticeStepEffectiveComplete, using the 20046 requirement
 * rules. This matters for a practice with a California billing address and no
 * California office that answered "No" (AC2): it only ever signs the standard
 * agreement, so the pre-20046 check would demand a Knox-Keene it will never
 * have and hold the practice out of activation.
 *
 * Reads the saved answer off the practice - unlike the display helpers, this
 * runs outside the Practice Agreement form.
 */
export const isPracticeStepEffectiveCompleteV2 = (
  practice: Practice,
): boolean => {
  const providesOnlineServicesInCalifornia =
    practice.hasOnlineServicesInCalifornia
  const isPracticeAgreementComplete =
    !practiceAgreementIsRequiredV2(providesOnlineServicesInCalifornia) ||
    practiceAgreementComplete(practice, true)
  const isKnoxKeeneComplete =
    !knoxKeeneIsRequiredV2(practice, providesOnlineServicesInCalifornia) ||
    knoxKeeneComplete(practice, true)
  return isPracticeAgreementComplete && isKnoxKeeneComplete
}

export const getPracticeProfileStep = (
  practiceProfileSteps: PracticeProfileStep[],
  stepName: PracticeStepNameEnum,
) => {
  return practiceProfileSteps.find(
    (practiceProfileStep) => practiceProfileStep.name === stepName,
  ) as PracticeProfileStep
}
